import { useCallback, useEffect, useState } from "react";
import partition from "lodash/partition";
import styled from "styled-components/macro";

import StudentNavMenu from "./StudentNavMenu";
import AlongLogo from "components/AlongLogo";
import Button from "ui/Button";
import { Icon } from "@chanzuckerberg/eds";
import { theme } from "ui/theme";

import sortReflections from "./sortReflections";
import { ReflectionType } from "./getReflectionTypeIcon";
import { useStudentNavigationQuery } from "./StudentNavigation.generated";
import { useLocation } from "react-router-dom";
import { useStudentCurrentUser } from "helpers/useCurrentUser";
import {
  useAnalytics,
  ActionType,
  AnalyticsProvider,
} from "utils/analytics/AnalyticsContext";
import { SurfaceType } from "utils/analytics/analyticsConstants";
import Tooltip from "ui/Tooltip";

const $MobileHeader = styled.header`
  background-color: ${theme.ColorWhite};
  border-bottom: 0.25rem solid ${theme.ColorSpace100};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  width: 100vw;
  z-index: ${theme.zIndexLevelOne};

  @media screen and (${theme.BreakpointTabletUp}) {
    display: none;
  }
`;

// Add an empty element on the right side of the mobile nav to mirror the
// left-hand button and ensure the logo is centered.
const $Spacer = styled.div`
  width: 2.5rem;
`;

const $MenuContainer = styled.nav<{ $isOpen: boolean }>`
  background-color: ${theme.ColorWhite};
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: ${({ $isOpen }) => ($isOpen ? "25" : "8")}rem;

  @media screen and (${theme.BreakpointTabletDown}) {
    ${({ $isOpen }) => !$isOpen && "display: none;"}
    width: 100vw;
    z-index: ${theme.zIndexLevelTwo};
  }
`;

const $DesktopToggleContainer = styled.div<{ $isOpen: boolean }>`
  display: flex;
  justify-content: ${({ $isOpen }) => ($isOpen ? "end" : "center")};
  padding: 1rem;

  @media screen and (${theme.BreakpointTabletDown}) {
    // Hide the desktop toggle on mobile devices.
    display: none;
  }
`;

// These styles are copied over from `pages/Composer/IconButton`. We should
// probably promote them to `components/` at some point, but Design hasn't
// defined how this style relates to our existing icon button styles.
const $IconButton = styled(Button).attrs({
  variant: "secondary",
})`
  background-color: transparent;
  border-color: transparent;
  padding: 0;
  min-height: 3rem;
  width: 3rem;

  &:active,
  &:focus,
  &:hover {
    background-color: ${theme.ColorBlurple100} !important;
    border-color: ${theme.ColorBlurple100} !important;
  }

  &:disabled,
  &.disabled {
    background-color: ${theme.ColorSneaker100} !important;
    border-color: ${theme.ColorSneaker100} !important;

    path {
      fill: ${theme.ColorSpace300};
    }
  }
`;

const STUDENT_NAV_KEY = "along-student-nav";

export function clearLocalNavState() {
  window.sessionStorage.removeItem(STUDENT_NAV_KEY);
}

function useStudenNavigationState() {
  const location = useLocation();
  const isMobile = window.matchMedia(`(${theme.BreakpointTabletDown})`).matches;

  // Use `sessionStorage` to temporarily store the open/close state on desktop.
  // This allows the nav to remain open even if the page reloads. Skip this
  // behavior on mobile devices.
  const initialState = !isMobile
    ? window.sessionStorage.getItem(STUDENT_NAV_KEY) === "true"
    : false;

  const [isOpen, setIsOpen] = useState(initialState);

  const setIsNavOpen = useCallback((isNavOpen: boolean) => {
    window.sessionStorage.setItem(STUDENT_NAV_KEY, String(isNavOpen));
    setIsOpen(isNavOpen);
  }, []);

  // When open, the mobile nav covers the entire screen. Close it whenever the
  // user navigates to a different route.
  useEffect(() => {
    isMobile && setIsNavOpen(false);
  }, [isMobile, location, setIsNavOpen]);

  return [isOpen, setIsNavOpen] as const;
}

export default function StudentNavigation() {
  const { trackEvent } = useAnalytics("StudentNavigation");
  const student = useStudentCurrentUser();
  const [isOpen, setIsOpen] = useStudenNavigationState();
  const { data, error, loading } = useStudentNavigationQuery({
    fetchPolicy: "cache-first",
  });
  const [focusedSection, setFocusedSection] = useState<ReflectionType | null>(
    null,
  );

  const MENU_ID = `student-menu-${student.id}`;

  const newReflections = data?.studentViewer.activeReflectionQuestions.nodes;
  const [expiredReflections, answeredReflections] = partition(
    data?.studentViewer.completedReflectionQuestions.nodes || [],
    "isExpired",
  );
  const allSessionsReceived =
    data?.studentViewer.statsForStudent.allSessionsReceived || 0;
  const allSessionsResponded =
    data?.studentViewer.statsForStudent.allSessionsResponded || 0;
  const notifications = data?.studentViewer.notifications;

  useEffect(() => {
    if (newReflections && notifications) {
      trackEvent({
        eventName: "student_inventory",
        eventType: ActionType.STATE,
        analyticsSurface: SurfaceType.NAVIGATION,
        extraProperties: {
          extraMetadata: {
            numActiveReflections: newReflections.length,
            numUnreadResponses: notifications.length,
          },
        },
      });
    }
  }, [newReflections, notifications, trackEvent]);

  const sections = [
    {
      reflections: sortReflections(newReflections || []),
      type: "New" as const,
      hasNotifications: newReflections?.some(
        (reflection) => reflection && !reflection.studentReadAt,
      ),
    },
    {
      reflections: sortReflections(answeredReflections || []),
      type: "Answered" as const,
      hasNotifications: answeredReflections?.some(
        (reflection) => reflection?.hasNotifications,
      ),
    },
    {
      defaultExpanded: false,
      reflections: sortReflections(expiredReflections || []),
      type: "Expired" as const,
    },
  ];

  const toggleNav = (section: ReflectionType | null) => {
    setFocusedSection(section);
    setIsOpen(!isOpen);
  };

  return (
    <AnalyticsProvider surface={SurfaceType.NAVIGATION}>
      {!isOpen && (
        <$MobileHeader>
          <Button
            analyticsName="clicked_open_mobile_nav"
            aria-controls={MENU_ID}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(true)}
            requestInProgress={false}
            variant="icon"
          >
            <Icon
              name="arrow-back"
              purpose="informative"
              title="Open navigation"
              size="1.5rem"
              color={theme.ColorSpace500}
            />
          </Button>
          <AlongLogo variant="bulb-mint" />
          <$Spacer />
        </$MobileHeader>
      )}
      <$MenuContainer id={MENU_ID} $isOpen={isOpen}>
        <StudentNavMenu
          focusedSection={focusedSection}
          hasError={!!error}
          isLoading={loading}
          isOpen={isOpen}
          menuId={MENU_ID}
          onToggleNav={toggleNav}
          sections={sections}
          stats={{
            allSessionsReceived,
            allSessionsResponded,
          }}
          student={student}
        />
        <$DesktopToggleContainer $isOpen={isOpen}>
          <Tooltip
            label={isOpen ? "Collapse Menu" : "Expand Menu"}
            placement="right"
          >
            <$IconButton
              analyticsName={
                isOpen ? "clicked_collapse_nav" : "clicked_expand_nav"
              }
              aria-controls={MENU_ID}
              aria-expanded={isOpen}
              onClick={() => toggleNav(null)}
              requestInProgress={false}
            >
              <Icon
                name={isOpen ? "chevron-left" : "chevron-right"}
                purpose="informative"
                title={isOpen ? "Close menu" : "Open menu"}
                size="1.25rem"
                color={theme.ColorSpace500}
              />
            </$IconButton>
          </Tooltip>
        </$DesktopToggleContainer>
      </$MenuContainer>
    </AnalyticsProvider>
  );
}
